import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, Globe, Instagram, ArrowLeft, Sprout, Heart, Users, Sparkles, Shield, Target } from "lucide-react";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import { SOCIAL } from "@/lib/social-links";
import { TikTokIcon, FacebookIcon } from "@/components/icons/SocialIcons";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/about" },
      { title: "About Jozori | من نحن — جذوري" },
      {
        name: "description",
        content:
          "Jozori is a warm, family-centered world that helps children grow rooted in values — gently, joyfully, and one week at a time.",
      },
      { property: "og:title", content: "About Jozori | من نحن" },
      {
        property: "og:description",
        content:
          "Rooted in values. Growing with confidence. Discover the message behind Jozori and the family experience we're building.",
      },
      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/about" }],
  }),

  component: AboutPage,
});

const COPY = {
  en: {
    nav_home: "Home",
    eyebrow: "Who we are",
    slogan: "A warm world for growing roots.",
    lede:
      "Jozori is a calm, family-centered world where children grow in values, feelings, and character — gently, and at their own pace.",
    who_title: "Who We Are — Jozori",
    who_paragraphs: [
      "At Jozori, we believe that a child's deepest values are shaped in the earliest years of life, through everyday emotions, conversations, and small meaningful experiences.",
      "Jozori is a warm and emotionally grounded world designed to help children develop values, social skills, and positive behaviors in a way that feels natural, engaging, and connected to their daily lives — for ages 4 to 12.",
      "Through stories, activities, and gentle daily challenges, we guide children step by step in their emotional and social growth, while helping parents build stronger, more mindful relationships with their children.",
      "At Jozori, we do not believe in rigid teaching or long lectures. We believe in connection, repetition with kindness, meaningful experiences, and creating an environment where children feel safe, loved, and understood.",
      "The project was thoughtfully developed with educators, child psychologists, and designers to create a modern Arabic experience that respects childhood, understands children's emotional needs, and helps them grow with confidence and balance.",
    ],
    mission_title: "Our Message",
    message_paragraphs: [
      "At Jozori, our message is to plant in every child deep, lasting roots of values — roots that hold them steady through life's challenges and gently guide them toward goodness and balanced growth.",
      "We believe today's child needs more than academics. They need their feelings nurtured, their behavior gently shaped, and their self-awareness awakened — all in a way that feels real, simple, and warmly loved.",
      "We create family-centered content that strengthens the bond between a child and their family, opens up honest conversation at home, and gives parents tangible tools to shape their children's character — without ever needing to be parenting experts.",
      "Our message is to make everyday values feel like a joyful, ongoing, natural part of family life — never a burden, never a lecture, never a scolding.",
      "Week by week we walk alongside your child, notice how they grow, honor what makes them unique, and help them see themselves through eyes of love and trust.",
      "In every story, every gentle activity, every conversation… we plant a seed — so that one day it becomes a tree, deeply rooted within them.",
    ],
    vision_title: "Our Vision",
    vision:
      "A generation of children who grow up emotionally rooted, confident in who they are, and proud of where they come from — supported by families who feel guided, never overwhelmed.",
    goals_title: "Our Goals",
    goals_paragraphs: [
      "At Jozori, our goal is to help children grow up feeling safe, loved, confident, and deeply connected to the values that shape who they become.",
      "We aim to support children in understanding their emotions, expressing themselves in healthy ways, and learning how to interact with others through kindness, respect, and empathy.",
      "Through stories, activities, and gentle daily challenges, we help children build social and life skills naturally and joyfully, in ways that fit their age and emotional development.",
      "We also believe that parenting is a shared journey. That's why we create practical, approachable experiences that help parents build warmer and more meaningful relationships with their children — without pressure or complexity.",
      "At Jozori, we do not focus on rigid lessons or direct instruction. We focus on small repeated experiences that quietly leave a lasting impact on a child's everyday life.",
      "And with every story, conversation, and shared moment, we hope children grow with more confidence, emotional awareness, calmness, and compassion toward themselves and others.",
    ],
    values_title: "What we care about",
    values: [
      { icon: Sprout, t: "Roots", d: "Helping every child feel grounded in who they are." },
      { icon: Users, t: "Family", d: "Parents are at the heart of the journey — never on the outside." },
      { icon: Sparkles, t: "Warmth", d: "Growth should feel calm, joyful, and human." },
      { icon: Heart, t: "Emotion", d: "Feelings are nurtured before behavior is corrected." },
      { icon: Shield, t: "Safety", d: "A protected, quiet space — always." },
    ],
    app_title: "What is Jozori?",
    app_desc:
      "Jozori is a gentle, family-centered world that walks alongside your child as they grow — through warm stories, simple activities, and meaningful conversations that nurture values, feelings, and character.",
    contact_title: "Get in touch",
    cta: "Back to home",
  },
  ar: {
    nav_home: "الرئيسية",
    eyebrow: "من نحن",
    slogan: "عالمٌ دافئ تنمو فيه الجذور.",
    lede:
      "«جذوري» عالمٌ هادئ يدور حول العائلة، ينمو فيه الطفل في القيم والمشاعر والشخصية… بلطف، وعلى مهله.",
    who_title: "من نحن — مشروع جذوري",
    who_paragraphs: [
      "«جذوري» عالمٌ هادئ يدور حول العائلة، ينمو فيه الطفل في القيم والمشاعر والشخصية… بلطف، وعلى مهله.",
      "نرافق الطفل من ٤ إلى ١٢ سنة بقصص دافئة، وحوارات صغيرة، ولحظات يومية تترك أثرًا.",
      "يُصنع كل شيء هنا بعناية، مع مختصّين في عالم الطفولة، ليصل إلى قلب طفلك كما يستحق.",
    ],
    mission_title: "رسالتنا",
    message_paragraphs: [
      "أن نرافق كل طفل بهدوء، ونزرع فيه جذورًا دافئة من القيم والمشاعر والثقة بالنفس.",
      "أن نمنح العائلة لحظات صغيرة تبقى — حوارات، قصصًا، وذكريات تكبر مع الطفل.",
      "لا برنامج صارم ولا منهج… بل عالمٌ دافئ يكبر بهدوء مع البيت.",
    ],
    vision_title: "رؤيتنا",
    vision:
      "أن نصنع مساحة يشعر فيها الطفل العربي، أينما كان، أن لغته وقيمه ما زالت قريبة منه.",
    goals_title: "ما نحلم به",
    goals_paragraphs: [
      "أن يكبر الطفل وهو يشعر بالأمان والمحبة والثقة بنفسه.",
      "أن تجد العائلة مساحة دافئة تجمعها حول قصة، أو حوار، أو لحظة صغيرة.",
      "أن تبقى اللغة العربية قريبة من قلب الطفل، أينما كان في العالم.",
    ],
    values_title: "ما الذي نهتم به",
    values: [
      { icon: Sprout, t: "الجذور", d: "نساعد كل طفل ليشعر بثبات هويته." },
      { icon: Users, t: "العائلة", d: "الأهل في قلب الرحلة، لا على هامشها." },
      { icon: Sparkles, t: "الدفء", d: "النمو يجب أن يكون هادئًا، ممتعًا، إنسانيًا." },
      { icon: Heart, t: "المشاعر", d: "نرعى المشاعر قبل أن نُقوّم السلوك." },
      { icon: Shield, t: "الأمان", d: "مساحة محمية وهادئة… دائمًا." },
    ],
    app_title: "ما هو «جذوري»؟",
    app_desc:
      "«جذوري» عالمٌ دافئ يدور حول العائلة، يرافق طفلك في نموّه عبر قصص حانية، وأنشطة بسيطة، وحوارات ذات معنى تُغذّي القيم والمشاعر والشخصية.",
    contact_title: "تواصل معنا",

    cta: "العودة إلى الرئيسية",
  },
} as const;


function AboutPage() {
  const { lang, dir, toggle } = useLang();
  const t = COPY[lang];

  const founder = lang === "ar"
    ? {
        eyebrow: "تعرّف على المؤسس",
        name: "بلال المداني",
        role: "مؤسس جذوري",
        paragraphs: [
          "يحمل بلال المداني درجة الماجستير في التسويق، ولديه خلفية أكاديمية في إدارة الأعمال، إلى جانب خبرة في تطوير العلامات التجارية والمشاريع الرقمية وبناء أفكار تجمع بين التكنولوجيا والتجارب الإنسانية الهادفة.",
          "أتاحت له تجربة العيش والعمل في بلدان مختلفة فهمًا قريبًا لتحدٍّ تشترك فيه الكثير من العائلات العربية: كيف نساعد أطفالنا على البقاء على صلة بلغتهم وقيمهم وهويتهم وشعورهم بالانتماء، وهم يكبرون في عالم رقمي سريع التغيّر.",
          "من هذا الفهم وُلدت جذوري — ليس كتطبيق تعليمي فحسب، بل كعالم رقمي دافئ يستطيع فيه الطفل أن يتعلّم وينمو ويبني ثقته بنفسه من خلال القصص والقيم واللغة والتجارب اليومية الهادفة.",
          "ومن خلال جذوري، يهدف بلال إلى مساعدة الأطفال على النمو وهم أكثر ارتباطًا بهويتهم وعائلاتهم وثقافتهم وقيمهم، وفي الوقت نفسه تقديم تجربة رقمية مدروسة يشعر الأهل بالاطمئنان وهم يجعلونها جزءًا من حياة أطفالهم.",
        ],
      }
    : {
        eyebrow: "Meet the Founder",
        name: "Bilal Almadani",
        role: "Founder, Jozori",
        paragraphs: [
          "Bilal Almadani holds a Master's degree in Marketing and a background in Business Administration, with experience in brand development, digital ventures, and building ideas that connect technology with meaningful human experiences.",
          "Living and working across different countries gave him a close understanding of a challenge shared by many Arab families: helping children stay connected to their language, values, identity, and sense of belonging while growing up in a rapidly changing digital world.",
          "From this understanding, Jozori was created — not simply as an educational app, but as a warm digital world where children can learn, grow, and build confidence through stories, values, language, and meaningful everyday experiences.",
          "Through Jozori, his goal is to help children grow with a stronger connection to their identity, family, culture, and values — while giving parents a thoughtful digital experience they can feel good about bringing into their children's lives.",
        ],
      };


  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
            >
              {lang === "en" ? "العربية" : "English"}
            </button>
            <Link
              to="/"
              className="hidden sm:inline-flex rounded-full bg-foreground px-3 py-1.5 text-xs font-bold text-background"
            >
              {t.nav_home}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/15"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {t.eyebrow}
          </p>
          <h1
            className={`mx-auto max-w-3xl text-4xl md:text-6xl font-black leading-tight ${
              lang === "ar" ? "font-display-ar" : "font-display-en"
            }`}
          >
            {t.slogan}
          </h1>
          <p className={`mx-auto mt-8 max-w-2xl text-base md:text-lg text-foreground/75 leading-relaxed ${lang === "ar" ? "font-display-ar leading-[2]" : ""}`}>
            {t.lede}
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <article className="rounded-3xl border border-clay/40 bg-card/60 p-8 md:p-12 shadow-sm backdrop-blur">
          <h2 className={`text-2xl md:text-3xl font-black mb-6 ${lang === "ar" ? "font-display-ar text-right" : "font-display-en"}`}>
            {t.who_title}
          </h2>
          <div className={`space-y-4 text-foreground/85 ${lang === "ar" ? "text-right leading-[2] font-display-ar text-[15px] md:text-base" : "leading-[1.85] text-[15px] md:text-base"}`}>
            {t.who_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </article>
      </section>


      {/* Our Message */}
      <section className="mx-auto max-w-3xl px-4 py-16">
        <article className="rounded-3xl border border-clay/40 bg-gradient-to-br from-primary/10 to-transparent p-8 md:p-12 shadow-sm">
          <div className="mb-4 inline-flex size-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Sprout className="size-5" />
          </div>
          <h2 className={`text-2xl md:text-3xl font-black mb-6 ${lang === "ar" ? "font-display-ar" : "font-display-en"}`}>
            {t.mission_title}
          </h2>
          <div className={`space-y-4 text-foreground/85 ${lang === "ar" ? "text-right leading-[2] font-display-ar text-[15px] md:text-base" : "leading-[1.85] text-[15px] md:text-base"}`}>
            {t.message_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </article>

        <article className="mt-6 rounded-3xl border border-clay/40 bg-gradient-to-br from-accent/15 to-transparent p-8 shadow-sm">
          <div className="mb-3 inline-flex size-10 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Sparkles className="size-5" />
          </div>
          <h2 className="text-2xl font-black mb-3">{t.vision_title}</h2>
          <p className={`text-foreground/80 leading-relaxed ${lang === "ar" ? "font-display-ar text-right leading-[2]" : ""}`}>{t.vision}</p>
        </article>
      </section>

      {/* Our Goals */}
      <section className="mx-auto max-w-3xl px-4 py-16">
        <article className="rounded-3xl border border-clay/40 bg-gradient-to-br from-secondary/10 to-transparent p-8 md:p-12 shadow-sm">
          <div className="mb-4 inline-flex size-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
            <Target className="size-5" />
          </div>
          <h2 className={`text-2xl md:text-3xl font-black mb-6 ${lang === "ar" ? "font-display-ar" : "font-display-en"}`}>
            {t.goals_title}
          </h2>
          <div className={`space-y-4 text-foreground/85 ${lang === "ar" ? "text-right leading-[2] font-display-ar text-[15px] md:text-base" : "leading-[1.85] text-[15px] md:text-base"}`}>
            {t.goals_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </article>
      </section>


      {/* Founder */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.04] to-transparent"
        />
        {/* Abstract monogram watermark */}
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none"
        >
          <span
            className="block text-[14rem] md:text-[22rem] font-black leading-none tracking-tighter"
            style={{
              background: "linear-gradient(135deg, rgba(59,130,246,0.05), rgba(16,185,129,0.03))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {lang === "ar" ? "ج" : "J"}
          </span>
        </div>

        <div className="relative mx-auto max-w-2xl px-4 py-16 md:py-20 text-center">
          <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.28em] text-primary/80">
            {founder.eyebrow}
          </p>
          <h2
            className={`text-2xl md:text-4xl font-bold tracking-tight mb-2 ${
              lang === "ar" ? "font-display-ar leading-snug" : "font-display-en leading-tight"
            }`}
          >
            {founder.name}
          </h2>
          <p className="text-sm font-medium text-foreground/55 tracking-wide mb-10">
            {founder.role}
          </p>
          <div className="mx-auto mb-10 h-px w-12 bg-primary/30" />

          <div className={`space-y-5 text-[15px] md:text-base text-foreground/75 max-w-[62ch] mx-auto ${lang === "ar" ? "text-right leading-[2] font-display-ar" : "text-left md:text-center leading-[1.8]"}`}>
            {founder.paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "text-foreground/90 font-medium" : ""}>{p}</p>
            ))}
          </div>
        </div>
      </section>



      {/* Values */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-center text-3xl md:text-4xl font-black mb-12">{t.values_title}</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {t.values.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.t}
                className="rounded-2xl border border-clay/40 bg-card p-6 text-center hover:shadow-md transition"
              >
                <div className="mx-auto mb-3 inline-flex size-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                  <Icon className="size-6" />
                </div>
                <h3 className="font-black text-lg mb-1">{v.t}</h3>
                <p className="text-sm text-foreground/70 leading-snug">{v.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* App description */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <div className="rounded-3xl bg-foreground text-background p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">{t.app_title}</h2>
          <p className="text-background/80 text-lg leading-relaxed max-w-2xl mx-auto">{t.app_desc}</p>
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="text-2xl md:text-3xl font-black mb-8">{t.contact_title}</h2>
        <div className="flex flex-wrap flex-col sm:flex-row gap-3 justify-center items-center">
          <a
            href="mailto:info@jozori.com"
            className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-3 font-bold hover:bg-clay/20"
          >
            <Mail className="size-4" /><span>info@jozori.com</span>
          </a>
          <a
            href="https://www.jozori.com"
            className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-3 font-bold hover:bg-clay/20"
          >
            <Globe className="size-4" /><span>www.jozori.com</span>
          </a>
          <a
            href={SOCIAL.instagram.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-3 font-bold hover:bg-clay/20"
          >
            <Instagram className="size-4" /><span>{SOCIAL.instagram.handle}</span>
          </a>
          <a
            href={SOCIAL.tiktok.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-3 font-bold hover:bg-clay/20"
          >
            <TikTokIcon className="size-4" /><span>{SOCIAL.tiktok.handle}</span>
          </a>
          <a
            href={SOCIAL.facebook.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-3 font-bold hover:bg-clay/20"
          >
            <FacebookIcon className="size-4" /><span>{SOCIAL.facebook.handle}</span>
          </a>
        </div>

        <div className="mt-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-black text-primary-foreground shadow hover:opacity-90"
          >
            <ArrowLeft className={`size-4 ${lang === "ar" ? "rotate-180" : ""}`} />
            {t.cta}
          </Link>
        </div>
      </section>

      <footer className="border-t border-clay/30 py-8 text-center text-xs text-foreground/60">
        © {new Date().getFullYear()} Jozori · جذوري
      </footer>
    </div>
  );
}
